import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { StatsChartProps } from '@ui/components/StatsCharts/StatsChart';
import { useTranslations } from '@ui/i18n';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { useMemo } from 'react';

import type { StatsMetricValue, StatsResponseType } from './types';

const endpoint = process.env.NEXT_PUBLIC_STATS_API ?? '';

type ResultType = {
  pid1HelpersEmployed: StatsMetricValue[];
  pid2HelpersEmployed: StatsMetricValue[];
  pid3HelpersEmployed: StatsMetricValue[];
  pid4HelpersEmployed: StatsMetricValue[];
  pid5HelpersEmployed: StatsMetricValue[];
};

async function fetchMetrics(
  startTimestamp: number,
  endTimestamp: number,
): Promise<ResultType> {
  const response: AxiosResponse<StatsResponseType> = await axios.post(
    endpoint,
    {
      endpointUrl: 'insights/cronos/veno/query',
      timeRange: {
        start: startTimestamp.toString(),
        end: endTimestamp.toString(),
        step: 86400,
      },
      queries: [
        {
          eventsQuery: {
            resource: {
              name: 'StakeNftCall',
              type: 'EVENTS',
            },
            alias: '',
            id: 'a',
            aggregation: {
              aggregateProperties: {
                type: 'CUMULATIVE_COUNT',
                propertyName: 'transaction_hash',
              },
            },
            selectorExpr: {
              logicExpr: {
                expressions: [],
                operator: 'AND',
              },
            },
            groupBy: ['pid'],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'UnstakeNftCall',
              type: 'EVENTS',
            },
            alias: '',
            id: 'b',
            aggregation: {
              aggregateProperties: {
                type: 'CUMULATIVE_COUNT',
                propertyName: 'transaction_hash',
              },
            },
            selectorExpr: null,
            groupBy: ['pid'],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
      ],
      formulas: [
        {
          expression: 'a-b',
          alias: 'pid {{pid}}',
          id: 'A',
          disabled: false,
          functions: [],
        },
      ],
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const results = response.data.results;

  if (!results.length) {
    console.error('invalid response from API for useStatsHelpersByGarden');
  }

  const pid1 =
    results
      .find((r) => r.id === 'A')
      ?.matrix?.samples?.find((s) => s.metric.labels['pid'] === '1')?.values ??
    [];
  const pid2 =
    results
      .find((r) => r.id === 'A')
      ?.matrix?.samples?.find((s) => s.metric.labels['pid'] === '2')?.values ??
    [];
  const pid3 =
    results
      .find((r) => r.id === 'A')
      ?.matrix?.samples?.find((s) => s.metric.labels['pid'] === '3')?.values ??
    [];
  const pid4 =
    results
      .find((r) => r.id === 'A')
      ?.matrix?.samples?.find((s) => s.metric.labels['pid'] === '4')?.values ??
    [];
  const pid5 =
    results
      .find((r) => r.id === 'A')
      ?.matrix?.samples?.find((s) => s.metric.labels['pid'] === '5')?.values ??
    [];

  return {
    pid1HelpersEmployed: pid1,
    pid2HelpersEmployed: pid2,
    pid3HelpersEmployed: pid3,
    pid4HelpersEmployed: pid4,
    pid5HelpersEmployed: pid5,
  };
}

export const useStatsHelpersByGarden = (
  startTimestamp: number,
  endTimestamp: number,
  options?: UseQueryOptions<ResultType>,
): StatsChartProps => {
  const t = useTranslations();
  const queryKey = ['useStatsHelpersByGarden', startTimestamp, endTimestamp];

  const result = useQuery({
    queryKey,
    queryFn: () => fetchMetrics(startTimestamp, endTimestamp),
    ...options,
  })?.data;

  return useMemo(
    () => ({
      title: t('Total Helpers Employed per Garden'),
      values: {
        'Ferro (CRO)': result?.pid1HelpersEmployed,
        'VVS (VNO)': result?.pid2HelpersEmployed,
        Tectonic: result?.pid3HelpersEmployed,
        'Ferro (ATOM)': result?.pid4HelpersEmployed,
        'VVS (CRO)': result?.pid5HelpersEmployed,
      },
      type: 'line',
    }),
    [result, t],
  );
};
